import { useEffect, useRef } from 'react';

import type { AIPresenceState } from '@/common/components/presence/types';
import {
  coalesceActivityPulse,
  getPulseLuminosityBoost,
  type ActivityPulseState,
} from '@/common/components/presence/nebula/nebulaActivityPulse';
import {
  createNebulaCanvasAnimator,
  type NebulaCanvasAnimator,
} from '@/common/components/presence/nebula/nebulaCanvasRenderer';
import { computePulseCanvasCssSize } from '@/common/components/presence/presenceGlowBudget';
import { useOrbVisibility } from '@/common/components/presence/useOrbVisibility';
import type { OrbPaletteStops } from '@/common/theme/orbPalette';

type NebulaOrbWebProps = {
  palette: OrbPaletteStops;
  state: AIPresenceState;
  size: number;
  activityPulse?: number;
  speechAmp?: number;
  speechPunch?: number;
};

/**
 * Frame-limited Canvas 2D Deep-Field Nebula renderer (web only).
 */
export function NebulaOrbWeb({
  palette, state, size, activityPulse = 0, speechAmp = 0, speechPunch = 0,
}: NebulaOrbWebProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const stateRef = useRef(state);
  const activityPulseRef = useRef(activityPulse);
  const speechAmpRef = useRef(speechAmp);
  const speechPunchRef = useRef(speechPunch);
  const pulseStateRef = useRef<ActivityPulseState | null>(null);
  const animatorRef = useRef<NebulaCanvasAnimator | null>(null);
  const { ref: visibilityRef, isVisible } = useOrbVisibility();
  const visibilityStateRef = useRef(isVisible);

  stateRef.current = state;
  speechAmpRef.current = speechAmp;
  speechPunchRef.current = speechPunch;
  visibilityStateRef.current = isVisible;

  useEffect(() => {
    if (activityPulse > activityPulseRef.current) {
      pulseStateRef.current = coalesceActivityPulse(pulseStateRef.current, performance.now());
      if (visibilityStateRef.current) {
        animatorRef.current?.drawNow(performance.now());
      }
    }
    activityPulseRef.current = activityPulse;
  }, [activityPulse]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const animator = createNebulaCanvasAnimator({
      canvas,
      size,
      palette,
      state: () => stateRef.current,
      pulseBoost: (at) => getPulseLuminosityBoost(pulseStateRef.current, at),
      speechAmp: () => speechAmpRef.current,
      speechPunch: () => speechPunchRef.current,
      now: () => performance.now(),
      requestFrame: (callback) => requestAnimationFrame(callback),
      cancelFrame: (id) => cancelAnimationFrame(id),
      devicePixelRatio: window.devicePixelRatio || 1,
    });
    animatorRef.current = animator;
    animator.setVisible(visibilityStateRef.current);

    return () => {
      animator.dispose();
      if (animatorRef.current === animator) animatorRef.current = null;
    };
  }, [palette, size]);

  useEffect(() => {
    animatorRef.current?.setVisible(isVisible);
  }, [isVisible]);

  useEffect(() => {
    if (visibilityStateRef.current) {
      animatorRef.current?.drawNow(performance.now());
    }
  }, [state, speechAmp]);

  const pulseCanvasCss = computePulseCanvasCssSize(size);

  return (
    <div
      ref={(node) => {
        visibilityRef.current = node;
      }}
      style={{
        width: size,
        height: size,
        position: 'relative',
        overflow: 'visible',
      }}
      aria-hidden="true"
      data-testid="nebula-orb-canvas-host"
    >
      <canvas
        ref={canvasRef}
        data-testid="nebula-orb-canvas"
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          width: pulseCanvasCss,
          height: pulseCanvasCss,
          marginLeft: -pulseCanvasCss / 2,
          marginTop: -pulseCanvasCss / 2,
          display: 'block',
          pointerEvents: 'none',
        }}
      />
    </div>
  );
}
